import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Calendar, Users, DollarSign, TrendingUp, Clock, Settings2,
  UserPlus, AlertCircle, ArrowUpRight, ArrowDownRight, Zap, Check,
  ShoppingCart, BarChart3, Star, Target, Package, Repeat,
  Activity, Building2, MessageSquare
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";

const AVAILABLE_METRICS = {
  today_revenue:       { label: "Today's Revenue",   icon: DollarSign,   format: (v) => `$${(v || 0).toLocaleString()}`, trend: 12, gradient: "from-rose-500 to-orange-400", iconBg: "bg-rose-50", iconColor: "text-rose-500" },
  today_appointments:  { label: "Today's Appts",     icon: Calendar,     format: (v) => v || 0, gradient: "from-violet-500 to-fuchsia-500", iconBg: "bg-violet-50", iconColor: "text-violet-500" },
  week_revenue:        { label: "Week Revenue",      icon: DollarSign,   format: (v) => `$${(v || 0).toLocaleString()}`, gradient: "from-rose-500 to-orange-400", iconBg: "bg-rose-50", iconColor: "text-rose-500" },
  week_appointments:   { label: "Week Appts",        icon: Calendar,     format: (v) => v || 0, gradient: "from-violet-500 to-fuchsia-500", iconBg: "bg-violet-50", iconColor: "text-violet-500" },
  month_revenue:       { label: "Month Revenue",     icon: DollarSign,   format: (v) => `$${(v || 0).toLocaleString()}`, gradient: "from-rose-500 to-orange-400", iconBg: "bg-rose-50", iconColor: "text-rose-500" },
  total_clients:       { label: "Total Clients",     icon: Users,        format: (v) => v || 0, trend: 8, gradient: "from-teal-400 to-cyan-500", iconBg: "bg-teal-50", iconColor: "text-teal-500" },
  active_clients:      { label: "Active Clients",    icon: Users,        format: (v) => v || 0, gradient: "from-teal-400 to-cyan-500", iconBg: "bg-teal-50", iconColor: "text-teal-500" },
  new_clients_month:   { label: "New Clients (Mo)",  icon: UserPlus,     format: (v) => v || 0, gradient: "from-blue-500 to-violet-500", iconBg: "bg-blue-50", iconColor: "text-blue-500" },
  average_ticket:      { label: "Avg Ticket",        icon: TrendingUp,   format: (v) => `$${(v || 0).toFixed(0)}`, gradient: "from-amber-400 to-orange-500", iconBg: "bg-amber-50", iconColor: "text-amber-500" },
  completed_appointments: { label: "Completed",      icon: Check,        format: (v) => v || 0, gradient: "from-emerald-400 to-teal-500", iconBg: "bg-emerald-50", iconColor: "text-emerald-500" },
  cancelled_appointments: { label: "Cancelled",      icon: AlertCircle,  format: (v) => v || 0, gradient: "from-rose-500 to-pink-500", iconBg: "bg-rose-50", iconColor: "text-rose-500" },
  noshow_appointments: { label: "No-Shows",          icon: AlertCircle,  format: (v) => v || 0, gradient: "from-amber-400 to-orange-500", iconBg: "bg-amber-50", iconColor: "text-amber-500" },
};

const DEFAULT_SLOTS = ["today_revenue", "today_appointments", "total_clients", "average_ticket"];
const STORAGE_KEY = "salonflow_dashboard_kpis";

function loadSlots() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length === 4 && parsed.every(k => AVAILABLE_METRICS[k])) return parsed;
    }
  } catch {}
  return DEFAULT_SLOTS;
}

const StatCard = ({ icon: Icon, label, value, trend, testId, editing, metricKey, onChangeMetric, gradient, iconBg, iconColor }) => (
  <div data-testid={testId} className={`bg-white border rounded-2xl p-6 transition-all duration-300 ${editing ? 'border-rose-300 shadow-lg shadow-rose-500/10' : 'border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5'}`}>
    {editing ? (
      <div>
        <Select value={metricKey} onValueChange={onChangeMetric}>
          <SelectTrigger className="h-9 border-slate-200 rounded-xl text-sm mb-3 focus:ring-rose-500/20" data-testid={`${testId}-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(AVAILABLE_METRICS).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={1.5} />
          </div>
          <p className="font-['Outfit'] text-xl font-semibold text-slate-900">{value}</p>
        </div>
      </div>
    ) : (
      <>
        <div className="flex items-start justify-between mb-4">
          <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={1.5} />
          </div>
          {trend && (
            <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${trend > 0 ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'}`}>
              {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <p className="font-['Outfit'] text-2xl font-semibold tracking-tight text-slate-900">{value}</p>
        <p className="text-sm text-slate-500 mt-1 font-['Manrope']">{label}</p>
      </>
    )}
  </div>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [openSlots, setOpenSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [kpiSlots, setKpiSlots] = useState(loadSlots);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        try {
          const seedStatus = await api.get("/seed/status");
          if (!seedStatus.data.seeded) {
            await api.post("/seed");
          }
        } catch {}
        const res = await api.get("/dashboard/stats");
        setStats(res.data);
        try {
          const slotsRes = await api.get("/dashboard/open-slots");
          setOpenSlots(slotsRes.data);
        } catch {}
      } catch (err) {
        console.error("Dashboard load error:", err);
        setStats({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateSlot = (index, newKey) => {
    setKpiSlots(prev => { const next = [...prev]; next[index] = newKey; return next; });
  };
  const saveLayout = () => { localStorage.setItem(STORAGE_KEY, JSON.stringify(kpiSlots)); setEditing(false); };
  const cancelEdit = () => { setKpiSlots(loadSlots()); setEditing(false); };
  const resetDefaults = () => { setKpiSlots([...DEFAULT_SLOTS]); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = stats || {};

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-slate-500 mt-1 font-['Manrope']">Here's how your salon is performing today</p>
        </div>
        {/* Quick action buttons */}
        <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-quick-actions">
          <button onClick={() => navigate("/location-pulse")} data-testid="quick-location-pulse"
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-slate-200 hover:border-rose-300 hover:shadow-sm transition-all text-sm font-medium text-slate-700 font-['Manrope'] group">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-rose-500 to-violet-500 flex items-center justify-center">
              <Activity className="w-3.5 h-3.5 text-white" strokeWidth={2} />
            </div>
            <span>Location Pulse</span>
          </button>
          <button onClick={() => navigate("/command-center")} data-testid="quick-command-center"
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-slate-200 hover:border-violet-300 hover:shadow-sm transition-all text-sm font-medium text-slate-700 font-['Manrope']">
            <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-violet-600" strokeWidth={2} />
            </div>
            <span>Command Center</span>
          </button>
          <button onClick={() => navigate("/calendar")} data-testid="quick-calendar"
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all text-sm font-medium text-slate-700 font-['Manrope']">
            <div className="w-6 h-6 rounded-lg bg-teal-100 flex items-center justify-center">
              <Calendar className="w-3.5 h-3.5 text-teal-600" strokeWidth={2} />
            </div>
            <span>Calendar</span>
          </button>
          <button onClick={() => navigate("/pos")} data-testid="quick-checkout"
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all text-sm font-medium text-slate-700 font-['Manrope']">
            <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center">
              <ShoppingCart className="w-3.5 h-3.5 text-amber-600" strokeWidth={2} />
            </div>
            <span>Checkout</span>
          </button>
        </div>
      </div>

      {/* KPI Controls */}
      <div className="mb-3 flex items-center justify-end gap-2">
        {editing ? (
          <>
            <Button variant="outline" size="sm" onClick={resetDefaults} data-testid="reset-kpi-btn"
              className="h-8 text-xs border-slate-200 rounded-xl px-3 text-slate-500 hover:bg-slate-50">
              Reset Defaults
            </Button>
            <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="cancel-kpi-btn"
              className="h-8 text-xs border-slate-200 rounded-xl px-3 text-slate-500 hover:bg-slate-50">
              Cancel
            </Button>
            <Button size="sm" onClick={saveLayout} data-testid="save-kpi-btn"
              className="h-8 text-xs gradient-primary text-white rounded-xl px-4 shadow-sm">
              <Check className="w-3 h-3 mr-1" />Save
            </Button>
          </>
        ) : (
          <button
            data-testid="customize-kpi-btn"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-rose-500 transition-colors px-2 py-1 rounded-lg hover:bg-rose-50"
          >
            <Settings2 className="w-3.5 h-3.5" />Customize
          </button>
        )}
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {kpiSlots.map((metricKey, i) => {
          const metric = AVAILABLE_METRICS[metricKey] || AVAILABLE_METRICS.today_revenue;
          return (
            <StatCard
              key={`${metricKey}-${i}`}
              testId={`stat-slot-${i}`}
              icon={metric.icon}
              label={metric.label}
              value={metric.format(s[metricKey])}
              trend={!editing ? metric.trend : undefined}
              editing={editing}
              metricKey={metricKey}
              onChangeMetric={(newKey) => updateSlot(i, newKey)}
              gradient={metric.gradient}
              iconBg={metric.iconBg}
              iconColor={metric.iconColor}
            />
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900">Revenue</h3>
              <p className="text-sm text-slate-400 font-['Manrope']">Last 7 days</p>
            </div>
            <div className="text-right">
              <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">${(s.week_revenue || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-400 font-['Manrope']">Week total</p>
            </div>
          </div>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.revenue_by_day || []}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                  axisLine={false} tickLine={false}
                  tick={{ fill: '#94A3B8', fontSize: 12, fontFamily: 'Manrope' }}
                />
                <YAxis
                  tickFormatter={(v) => `$${v}`}
                  axisLine={false} tickLine={false}
                  tick={{ fill: '#94A3B8', fontSize: 12, fontFamily: 'Manrope' }}
                />
                <Tooltip
                  formatter={(v) => [`$${v}`, 'Revenue']}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #F1F5F9', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', fontFamily: 'Manrope' }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#F43F5E" strokeWidth={2.5} fill="url(#revenueGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="month-revenue-card">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-3 font-['Manrope']">Month Revenue</p>
            <p className="font-['Outfit'] text-3xl font-semibold text-slate-900">${(s.month_revenue || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="completed-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 font-['Manrope']">Completed</p>
            </div>
            <p className="font-['Outfit'] text-2xl font-semibold text-emerald-600">{s.completed_appointments || 0}</p>
            <p className="text-xs text-slate-400 mt-1 font-['Manrope']">appointments this week</p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="cancellations-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center">
                <AlertCircle className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 font-['Manrope']">Issues</p>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="font-['Outfit'] text-xl font-semibold text-rose-500">{s.cancelled_appointments || 0}</p>
                <p className="text-xs text-slate-400 font-['Manrope']">Cancelled</p>
              </div>
              <div>
                <p className="font-['Outfit'] text-xl font-semibold text-amber-500">{s.noshow_appointments || 0}</p>
                <p className="text-xs text-slate-400 font-['Manrope']">No-shows</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Services & Client Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="top-services">
          <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-4">Top Services</h3>
          <div className="space-y-3">
            {(s.top_services || []).map((svc, i) => (
              <div key={i} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-semibold ${
                    i === 0 ? 'gradient-primary text-white' : i === 1 ? 'bg-violet-50 text-violet-500' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="text-sm text-slate-700 font-medium font-['Manrope']">{svc.name}</span>
                </div>
                <span className="text-sm text-slate-400 font-['Manrope']">{svc.count} bookings</span>
              </div>
            ))}
            {(!s.top_services || s.top_services.length === 0) && (
              <p className="text-sm text-slate-400 font-['Manrope']">No data yet</p>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="active-clients">
          <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-4">Client Pipeline</h3>
          <div className="space-y-3">
            {[
              { stage: "Active", count: s.active_clients || 0, color: "#14B8A6" },
              { stage: "New This Month", count: s.new_clients_month || 0, color: "#8B5CF6" },
              { stage: "Total", count: s.total_clients || 0, color: "#F43F5E" },
            ].map((item) => (
              <div key={item.stage} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-slate-500 font-['Manrope']">{item.stage}</span>
                </div>
                <span className="text-sm font-semibold text-slate-800 font-['Manrope']">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Book Widget */}
      {openSlots.length > 0 && (
        <div className="mt-8 bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="quick-book-widget">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center shadow-sm">
                <Zap className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900">Quick Book</h3>
                <p className="text-xs text-slate-400 font-['Manrope']">Today's open slots</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {openSlots.slice(0, 6).map(provider => (
              <div key={provider.user_id} className="border border-slate-100 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-semibold shadow-sm" style={{ backgroundColor: provider.color }}>
                    {provider.name?.[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 font-['Manrope']">{provider.name}</p>
                    <p className="text-xs text-slate-400 capitalize font-['Manrope']">{provider.role?.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {provider.available_slots.map(time => (
                    <button
                      key={time}
                      data-testid={`quick-book-${provider.user_id}-${time}`}
                      onClick={() => navigate('/calendar')}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 hover:bg-rose-500 hover:text-white transition-all duration-200"
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
